import React, { Component } from 'react';

import Loading from '@qality/common-components/src/atoms/Loading/Loading';
import { QALITY_TEST_ISSUE_TYPE } from '@qality/common-config/src/constants';
import { messages } from '@qality/common-messages';
import { Issue, JiraPermissions, User } from '@qality/common-model';
import { QAlityIssueViewContextInitProps } from '@qality/common-model/src/TestCaseFullScreenData';
import {
  CREATE_ATTACHMENTS,
  DELETE_ALL_ATTACHMENTS,
  DELETE_OWN_ATTACHMENTS,
  IssueService,
  PERMISSIONS_EDIT_ISSUES,
  PermissionService,
  StorageOfUniqueServices,
  UserService,
} from '@qality/common-service';

type Props = {
  children: React.ReactChild;
  initProps?: QAlityIssueViewContextInitProps;
};

type State = {
  currentUser: User;
  currentIssue?: Issue;
  currentUserPermissions: JiraPermissions;
  canEdit: boolean;
  isConfigurationDone: boolean;
  canDeleteAllAttachment: boolean;
  canCreateAttachment: boolean;
  canDeleteOwnAttachment: boolean;
  issue?: Issue;
};

const defaultState: State = {
  issue: null,
  currentUser: null,
  canEdit: false,
  isConfigurationDone: true,
  canDeleteAllAttachment: false,
  canCreateAttachment: false,
  canDeleteOwnAttachment: false,
  currentUserPermissions: null,
  currentIssue: null,
};

export const QAlityIssueViewContext = React.createContext<State>(defaultState);

export class QAlityIssueViewContextProvider extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = defaultState;
  }

  initFromProps(): void {
    const { currentIssue, currentUser, currentUserPermissions } =
      this.props.initProps;

    const canEdit =
      currentUser.getIdentifier() === 'anonymous_user'
        ? false
        : currentUserPermissions[PERMISSIONS_EDIT_ISSUES];

    this.setState({
      issue: currentIssue,
      currentUser,
      canEdit,
      isConfigurationDone: true,
      canDeleteAllAttachment: currentUserPermissions[DELETE_ALL_ATTACHMENTS],
      canCreateAttachment: currentUserPermissions[CREATE_ATTACHMENTS],
      canDeleteOwnAttachment: currentUserPermissions[DELETE_OWN_ATTACHMENTS],
    });
  }

  async initDefault(): Promise<void> {
    const issueKey = await StorageOfUniqueServices.get(
      'ContextParamsService'
    ).getIssueKey();
    const issue = await IssueService.getIssue(issueKey);
    const promises = [
      UserService.getCurrentUser(),
      this.getPermissions(issueKey),
      this.determineIsConfigurationDone(issue),
    ] as const;

    Promise.all(promises)
      .then((values) => {
        const [currentUser, currentUserPermissions, isConfigurationDone]: [
          User,
          JiraPermissions,
          boolean
        ] = values;
        const canEdit =
          currentUser.getIdentifier() === 'anonymous_user'
            ? false
            : currentUserPermissions[PERMISSIONS_EDIT_ISSUES];

        this.setState({
          issue,
          currentUser,
          canEdit,
          isConfigurationDone,
          canDeleteAllAttachment:
            currentUserPermissions[DELETE_ALL_ATTACHMENTS],
          canCreateAttachment: currentUserPermissions[CREATE_ATTACHMENTS],
          canDeleteOwnAttachment:
            currentUserPermissions[DELETE_OWN_ATTACHMENTS],
          currentUserPermissions,
        });
        return null;
      })
      .catch((e) => {
        StorageOfUniqueServices.get('ErrorHandlingUtils').handleCriticalError(
          e,
          messages['error.qalityIssueViewContextProvider.init.message'],
          plugin.servicedeskUrl
        );
      });
  }

  async componentDidMount(): Promise<void> {
    if (this.props.initProps) {
      this.initFromProps();
      return;
    }

    this.initDefault();
  }

  async determineIsConfigurationDone(issue: Issue): Promise<boolean> {
    const allowedIssueTypes = await IssueService.getCreateIssueMetadata(
      issue.fields.project.key
    );
    return allowedIssueTypes.some(
      (issueType) =>
        issueType.name === 'Test case' || //old issue type name
        issueType.name === QALITY_TEST_ISSUE_TYPE
    );
  }

  async getPermissions(issueKey: string): Promise<JiraPermissions> {
    return await PermissionService.hasPermissions(issueKey);
  }

  render(): JSX.Element {
    const { children } = this.props;

    return (
      <QAlityIssueViewContext.Provider
        value={{
          currentIssue: this.state.issue,
          currentUser: this.state.currentUser,
          currentUserPermissions: this.state.currentUserPermissions,
          canEdit: this.state.canEdit,
          isConfigurationDone: this.state.isConfigurationDone,
          canDeleteAllAttachment: this.state.canDeleteAllAttachment,
          canCreateAttachment: this.state.canCreateAttachment,
          canDeleteOwnAttachment: this.state.canDeleteOwnAttachment,
        }}
      >
        {this.state.issue ? (
          children
        ) : (
          <Loading relative spinnerSize='large' fontSize='large' />
        )}
      </QAlityIssueViewContext.Provider>
    );
  }
}

export const QAlityIssueViewConsumer = QAlityIssueViewContext.Consumer;
